import { useEffect, useMemo, useRef, useState } from "react";
import { MainContentPaddingTypes, NewModal } from "../BaseComponents";
import { useAppSelector } from "../../Store";
import { Graph_QL, System, useSection, Location } from "../../Services";
import { colors, fonts, glyphs } from "../../Styles";
import { useAttentionLog } from "../../Services/Agrisys_GqlDataCaller/AttentionLogCalls/useAttentionLog";
import { useTranslation } from "react-i18next";
import { AttentionTypes } from "../../Definitions";
import { LocaleFarmDate } from "../../Definitions/localeFarmDate";
import NewButton from "../BaseComponents/newButton";
import { PenModal } from "../LocationFeeder";

interface SingleAttentionModal_Props{
  clickedLog: number;
  modalState: boolean;
  onClose: () => void;
  systemId: number | string
  system: System;
}

export default function SingleAttentionModal({ modalState, onClose, systemId, clickedLog, system}: SingleAttentionModal_Props) {
  const { t } = useTranslation();
  const [activeLogId, setActiveLogId] = useState<number>(clickedLog);
  const [activePen, setActivePen] = useState<Location | undefined>();
  const [technicalDataToggled, setTechnicalDataToggled] = useState(false);
  
  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({ domain: farm })), [farm]);
  
  const { data: attentionLogState, maintainEntries: dismissAttentionLog } = useAttentionLog(connection);
  const { data: lfSectionsState } = useSection(connection);

  // Location
  const sections = useMemo(() => lfSectionsState[domain]?.data, [domain, lfSectionsState]);
  const location = sections?.map(section => section.section_locations).flat().find(loc => loc.location_id === activePen?.location_id);
  
  // Log Data
  const logData = useMemo(() => {
    if (domain in attentionLogState && attentionLogState[domain] && typeof attentionLogState[domain] === "object" && "data" in attentionLogState[domain] && attentionLogState[domain].data && systemId) {
      return attentionLogState[domain].data.filter((log) => log.fk_systems === +systemId && log.dismissed !== true);
    }
    return []
  }, [attentionLogState, domain, systemId])

  // Current Index
  const currentIndex = useMemo(() => {
    return logData.findIndex(logData => logData.attention_log_id === activeLogId);
  }, [logData, activeLogId]);

  // #region | Level and Type to String
  const levelToString: Record<number, string> = {
    0: t(`systemControls.states.${AttentionTypes.alert}`, { defaultValue: "alert" }),
    1: t(`systemControls.states.${AttentionTypes.warning}`, { defaultValue: "warning" })
  };

  const typeToString: Record<number, string> = {
    1: t("systemControls.general.system", {defaultValue: "System"}),
    2: t("systemControls.general.motor", {defaultValue: "Motor"}),
    3: t("systemControls.general.animal", {defaultValue: "Animal"})
  }
  // #endregion

  // #region | Technical Data Toggle and Height Calculation
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  
  useEffect(() => {
    if (contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    }
  }, [technicalDataToggled, activeLogId]);
  // #endregion

  // #region | Active Log
  const activeLog = useMemo(() => {
    const log = logData.find(log => log.attention_log_id === activeLogId);
    if (!log) return null;

    const name = log.additionalInformation?.controller_name; 
    const cpu = log.additionalInformation?.lan_controller_cpu_id;
    const pen = (log.location_name !== undefined && log.location_name !== null) || (log.fk_locations !==  undefined && log.fk_locations !== null) ?  {Pen:log?.location_name ?? log?.fk_locations } : undefined
   
    const displayData = {
      Time: new LocaleFarmDate(log.timestamp * 1000).toLocaleFarmString(),
      Type: typeToString[log.type],
      Level: levelToString[log.level],
      Message: log.message,
    };

    const locationId = log?.fk_locations !== null && log?.fk_locations !== undefined ? {Location_id: log.fk_locations} : undefined; 
    const ioDevice = log?.additionalInformation?.device_cpu_id !== undefined && log.additionalInformation.device_cpu_id !== null ? {"Io-device": `${log.additionalInformation.device_cpu_id}` + (log.additionalInformation.device_name !== undefined ? ` (${log.additionalInformation.device_name})` : "")} : undefined;
    const port = log?.additionalInformation?.port !== undefined && log.additionalInformation.port !== null ? {Port: `${log.additionalInformation.port}` + (log.additionalInformation.terminal_type !== undefined ? ` (${log.additionalInformation.terminal_type})` : "")} : undefined;
    const location = sections.map(section => section.section_locations).flat().find(loc => loc.location_id === log.fk_locations);
    
    const technicalData = {
      Attention_log_Id: log.attention_log_id,
      Lan_controller_log_id: log.lan_controller_log_id,
      ...locationId,
      Lan_controller: cpu !== undefined ? `${cpu}` + (name ? ` (${name})` : "") : null,
      ...ioDevice,
      ...port
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: "20px", alignItems: "center" }}>
        
        {/* General Info */}
        <div style={{display: "flex", flexDirection: "column", gap: "10px", width: "250px"}}>
          {pen &&
            <div style={{ display: "grid", gridTemplateColumns: "80px 1fr", gap: "30px" }}>
              <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, ...fonts.fontWeight2 }}>Pen:</p>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, ...fonts.fontWeight1 }}>{log.location_name}</p>
                <NewButton content={"go to pen"} onClick={() => setActivePen(location)} overrides={{ baseStyles: { width: "fit-content", height: "fit-content", padding: "4px 8px" } }} /></div>
            </div>
          }
          {Object.entries(displayData).map(([label, value]) => (
            <div key={label} style={{ display: "grid", gridTemplateColumns: "80px 1fr", gap: "30px" }}>
              <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, ...fonts.fontWeight2 }}> {`${label.replace(/_/g, " ")}:`} </p>
              <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary }}>
                {String(value)}
              </p>
            </div>
          ))}
        </div>
        
        {/* Technical Data */}
        <div style={{backgroundColor: colors.blueDarkSecondary, display: "flex", flexDirection: "column", gap: "10px", width: "270px", padding: "10px", borderRadius: "10px", boxSizing: "border-box", overflow: "hidden", height: technicalDataToggled ? height + 45 : 35, transition: "0.3s"}}>
          <div style={{ display: "flex", cursor: "pointer", alignItems: "center", WebkitTapHighlightColor: "transparent", gap: "5px"}} onClick={() => setTechnicalDataToggled(!technicalDataToggled)} >
            <p style={{...fonts.header,...fonts.fontSize4, color: colors.greyLightTertiary,marginBottom: "0px"}}> {t(`systemControls.general.technicalData`, { defaultValue: "Technical data" })} </p>
            <glyphs.Arrow transparency={technicalDataToggled ? 1 : 0.3} rotate={technicalDataToggled ? 90 : 0} transition={0.3} size="15px" />
          </div>
          {/* <div ref={contentRef}> */}
            <div ref={contentRef} style={{display: "flex", flexDirection: "column", gap: "10px", opacity: technicalDataToggled ? 1 : 0, overflow: "hidden", transition: "0.3s"}}>
              {Object.entries(technicalData).map(([label, value]) => (
                <div key={label} style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "30px" }}>
                  <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, ...fonts.fontWeight2 }}> {`${label.replace(/_/g, " ")}:`} </p>
                  <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary }}>
                    {String(value)}
                  </p>
                </div>
              ))}
            </div>
          {/* </div> */}
        </div>

      </div>
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logData, activeLogId, technicalDataToggled]);
  // #endregion

  return (
    <NewModal
      modalState={modalState}
      header={t("systemControls.general.attention", {defaultValue: "attention"})}
      subHeader={activePen !== undefined ? `${t("locationFeederPages.locationFeeder.pen", { defaultValue: "Pen" })} ${location?.name}` : undefined}
      overrides={{
        backgroundColor: activePen !== undefined ? colors.greyLightTertiary : colors.blueDarkPrimary,
        mainContentPadding: MainContentPaddingTypes.primary,
        mainContentScroll: true,
        headerHeight: activePen !== undefined ? "100px" : "60px",
        headerContentHeight: activePen !== undefined ? "100px" : "20px"
      }}
      predefinedButtons={{
        onClose: onClose,
        onGoBack: activePen !== undefined ? () => setActivePen(undefined) : undefined,
        onNext: activePen !== undefined ? undefined : () => { setActiveLogId(logData[(currentIndex + 1) % logData.length].attention_log_id); setTechnicalDataToggled(false); },
        onPrev: activePen !== undefined ? undefined : () => { setActiveLogId(logData[(currentIndex - 1 + logData.length) % logData.length].attention_log_id); setTechnicalDataToggled(false); }, 
      }}
      mainContent={
        activePen !== undefined ? <div style={{ width: "735px", display: "flex", flexDirection: "column", alignItems: "center" }}>
          {system && <PenModal activePen={activePen} system={system} />}
        </div>
        : activeLog ? <div style={{ borderTop: "2px solid " + colors.blueDarkSecondary, paddingTop: "20px" }}>{activeLog}</div>
        : undefined
      }
      footerContent={ activePen !== undefined ? undefined :
        <NewButton
          content={t("systemControls.general.delete", {defaultValue: "delete"})}
          onClick={() => {
            if (dismissAttentionLog) { dismissAttentionLog({ data: [activeLogId] }) }; setActiveLogId(logData[(currentIndex + 1) % logData.length].attention_log_id)
          }}
        />
      }
    />
  ) 
}